import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';

interface OnlineUser {
  user: {
    id: number;
    name: string;
    email: string;
  };
  login_at: string;
  last_activity_at: string;
  device_info: string;
  ip_address: string;
  session_count: number;
}

interface SessionRecord {
  id: number;
  user: {
    id: number;
    name: string;
    email: string;
  };
  session_token: string;
  ip_address: string;
  user_agent: string;
  device_type: string;
  browser: string;
  platform: string;
  login_at: string;
  last_activity_at: string;
  logout_at: string | null;
  is_active: boolean;
  logout_reason: string | null;
  session_duration: number | null;
  device_info: string;
}

interface UniqueUser {
  id: number;
  name: string;
  email: string;
  is_admin: boolean;
  user_role_id: number;
  total_sessions: number;
  latest_session: {
    login_at: string;
    last_activity_at: string;
    logout_at: string | null;
    is_active: boolean;
    device_info: string;
    ip_address: string;
  } | null;
  is_currently_online: boolean;
}

interface SessionStats {
  total_sessions: number;
  unique_users: number;
  active_sessions: number;
  currently_online: number;
  average_session_duration: number;
  average_session_duration_formatted?: string;
  device_breakdown: Record<string, number>;
  browser_breakdown: Record<string, number>;
}

interface DashboardData {
  stats: SessionStats;
  online_users: OnlineUser[];
  hourly_logins: Record<string, number>;
  period: string;
  date_range: {
    start: string;
    end: string;
  };
}

const AdminSessionDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'today' | 'week' | 'month'>('today');

  // New state for collapsible sections
  const [allSessionsExpanded, setAllSessionsExpanded] = useState(false);
  const [uniqueUsersExpanded, setUniqueUsersExpanded] = useState(false);
  const [allSessions, setAllSessions] = useState<SessionRecord[]>([]);
  const [uniqueUsers, setUniqueUsers] = useState<UniqueUser[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [sessionsPagination, setSessionsPagination] = useState<any>(null);
  const [usersPagination, setUsersPagination] = useState<any>(null);

  const { token } = useSelector((state: RootState) => state.auth);
  const { user: currentUser } = useSelector((state: RootState) => state.auth);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/react-sessions/dashboard?period=${period}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const result = await response.json();
      if (result.success) {
        setDashboardData(result.data);
        setError(null);
      } else {
        throw new Error(result.error || 'Failed to fetch data');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [period, token]);

  // Force logout a user
  const forceLogoutUser = async (userId: number, userName: string) => {
    if (!window.confirm(`Are you sure you want to force logout ${userName}?`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/react-sessions/user/${userId}/force-logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      const result = await response.json();
      if (result.success) {
        alert(`${userName} has been logged out successfully`);
        fetchDashboardData(); // Refresh data
      } else {
        alert(`Failed to logout user: ${result.error}`);
      }
    } catch (err) {
      alert('An error occurred while logging out the user');
    }
  };

  // Force logout inactive sessions
  const forceLogoutInactive = async () => {
    if (!window.confirm('Are you sure you want to logout all inactive sessions (60+ minutes)?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/react-sessions/force-logout-inactive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ inactive_minutes: 60 }),
      });

      const result = await response.json();
      if (result.success) {
        alert(`${result.logged_out_count} inactive sessions have been logged out`);
        fetchDashboardData(); // Refresh data
      } else {
        alert(`Failed to logout inactive sessions: ${result.error}`);
      }
    } catch (err) {
      alert('An error occurred while logging out inactive sessions');
    }
  };

  // Fetch all sessions
  const fetchAllSessions = async (page = 1) => {
    setSessionsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/react-sessions/all?page=${page}&per_page=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sessions');
      }

      const result = await response.json();
      if (result.success) {
        setAllSessions(result.data.data);
        setSessionsPagination(result.data);
      } else {
        throw new Error(result.error || 'Failed to fetch sessions');
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  };

  // Fetch unique users
  const fetchUniqueUsers = async (page = 1) => {
    setUsersLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/react-sessions/users?page=${page}&per_page=10`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const result = await response.json();
      if (result.success) {
        setUniqueUsers(result.data.data);
        setUsersPagination(result.data);
      } else {
        throw new Error(result.error || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  // Toggle sections and fetch data when expanded
  const toggleAllSessions = () => {
    const newExpanded = !allSessionsExpanded;
    setAllSessionsExpanded(newExpanded);
    if (newExpanded && allSessions.length === 0) {
      fetchAllSessions();
    }
  };

  const toggleUniqueUsers = () => {
    const newExpanded = !uniqueUsersExpanded;
    setUniqueUsersExpanded(newExpanded);
    if (newExpanded && uniqueUsers.length === 0) {
      fetchUniqueUsers();
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [period, fetchDashboardData]);

  // Set up auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchDashboardData, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [fetchDashboardData]);

  // Format time ago
  const timeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  // Format session duration
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
            <div className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</div>
            <div className="mt-3">
              <button
                onClick={fetchDashboardData}
                className="bg-red-100 hover:bg-red-200 dark:bg-red-800 dark:hover:bg-red-700 text-red-800 dark:text-red-200 px-3 py-1 rounded text-sm"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return <div>No data available</div>;
  }

  const { stats, online_users } = dashboardData;

  return (
    <div className="space-y-6 p-4 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            React App Sessions Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Monitor user sessions and activity in the React approver app
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          {/* Period selector */}
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as 'today' | 'week' | 'month')}
            className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full sm:w-auto"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={forceLogoutInactive}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 sm:px-4 py-2 rounded-md text-sm font-medium flex-1 sm:flex-none"
            >
              <span className="hidden sm:inline">Logout Inactive</span>
              <span className="sm:hidden">Logout</span>
            </button>

            <button
              onClick={fetchDashboardData}
              className="bg-blue-500 hover:bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-md text-sm font-medium flex-1 sm:flex-none"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Currently Online</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.currently_online}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sessions</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.total_sessions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Unique Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.unique_users}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
              <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Session</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {stats.average_session_duration_formatted || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Currently Online Users */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Currently Online Users ({online_users.length})
          </h3>
        </div>
        <div className="p-4 sm:p-6">
          {online_users.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">No users currently online</p>
          ) : (
            <div className="space-y-4">
              {online_users.map((user) => (
                <div
                  key={user.user.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg gap-4"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 dark:text-green-400 font-medium text-sm">
                        {user.user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 dark:text-white truncate">{user.user.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.user.email}</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                    <div className="text-left sm:text-right">
                      <p className="text-sm text-gray-900 dark:text-white">{user.device_info}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Last active: {timeAgo(user.last_activity_at)}
                      </p>
                    </div>

                    <div className="text-left sm:text-right">
                      <p className="text-sm text-gray-900 dark:text-white">{user.ip_address}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {user.session_count} session{user.session_count !== 1 ? 's' : ''}
                      </p>
                    </div>

                    <button
                      onClick={() => forceLogoutUser(user.user.id, user.user.name)}
                      disabled={user.user.id === currentUser?.id}
                      className={`px-3 py-1 rounded text-sm font-medium w-full sm:w-auto ${
                        user.user.id === currentUser?.id
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-50'
                          : 'bg-red-500 hover:bg-red-600 text-white'
                      }`}
                      title={user.user.id === currentUser?.id ? "You cannot logout yourself" : "Force logout this user"}
                    >
                      {user.user.id === currentUser?.id ? 'You' : 'Force Logout'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Device and Browser Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Device Types</h3>
          </div>
          <div className="p-4 sm:p-6">
            {Object.entries(stats.device_breakdown).map(([device, count]) => (
              <div key={device} className="flex justify-between items-center py-2">
                <span className="text-gray-900 dark:text-white capitalize">{device}</span>
                <span className="font-medium text-gray-900 dark:text-white">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Browsers</h3>
          </div>
          <div className="p-4 sm:p-6">
            {Object.entries(stats.browser_breakdown).map(([browser, count]) => (
              <div key={browser} className="flex justify-between items-center py-2">
                <span className="text-gray-900 dark:text-white">{browser}</span>
                <span className="font-medium text-gray-900 dark:text-white">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* All Sessions Section - Collapsible */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div
          className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={toggleAllSessions}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              All Sessions ({stats.total_sessions})
            </h3>
            <svg
              className={`w-5 h-5 text-gray-500 transform transition-transform ${allSessionsExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {allSessionsExpanded && (
          <div className="p-4 sm:p-6">
            {sessionsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : allSessions.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">No sessions found</p>
            ) : (
              <div className="space-y-4">
                {allSessions.map((session) => (
                  <div
                    key={session.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg gap-4"
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        session.is_active ? 'bg-green-100 dark:bg-green-900' : 'bg-gray-100 dark:bg-gray-600'
                      }`}>
                        <span className={`font-medium text-sm ${
                          session.is_active ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {session.user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 dark:text-white truncate">{session.user.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{session.user.email}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-900 dark:text-white font-medium">
                          {session.is_active ? 'Active' : 'Inactive'}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400">{session.device_info}</p>
                      </div>
                      <div>
                        <p className="text-gray-900 dark:text-white">{session.ip_address}</p>
                        <p className="text-gray-500 dark:text-gray-400">
                          Login: {formatDate(session.login_at)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-900 dark:text-white">
                          Duration: {formatDuration(session.session_duration)}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400">
                          Last: {timeAgo(session.last_activity_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Pagination for sessions */}
                {sessionsPagination && sessionsPagination.last_page > 1 && (
                  <div className="flex justify-center space-x-2 mt-6">
                    <button
                      onClick={() => fetchAllSessions(sessionsPagination.current_page - 1)}
                      disabled={sessionsPagination.current_page === 1}
                      className="px-3 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-gray-700 dark:text-gray-300">
                      Page {sessionsPagination.current_page} of {sessionsPagination.last_page}
                    </span>
                    <button
                      onClick={() => fetchAllSessions(sessionsPagination.current_page + 1)}
                      disabled={sessionsPagination.current_page === sessionsPagination.last_page}
                      className="px-3 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* All Unique Users Section - Collapsible */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div
          className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={toggleUniqueUsers}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              All Unique Users ({stats.unique_users})
            </h3>
            <svg
              className={`w-5 h-5 text-gray-500 transform transition-transform ${uniqueUsersExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {uniqueUsersExpanded && (
          <div className="p-4 sm:p-6">
            {usersLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : uniqueUsers.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">No users found</p>
            ) : (
              <div className="space-y-4">
                {uniqueUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg gap-4"
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        user.is_currently_online ? 'bg-green-100 dark:bg-green-900' : 'bg-gray-100 dark:bg-gray-600'
                      }`}>
                        <span className={`font-medium text-sm ${
                          user.is_currently_online ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900 dark:text-white truncate">{user.name}</p>
                          {user.is_admin && (
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded">
                              Admin
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-900 dark:text-white font-medium">
                          {user.is_currently_online ? 'Online' : 'Offline'}
                        </p>
                        <p className="text-gray-500 dark:text-gray-400">
                          {user.total_sessions} session{user.total_sessions !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div>
                        {user.latest_session && (
                          <>
                            <p className="text-gray-900 dark:text-white">{user.latest_session.device_info}</p>
                            <p className="text-gray-500 dark:text-gray-400">{user.latest_session.ip_address}</p>
                          </>
                        )}
                      </div>
                      <div className="flex items-center">
                        {user.latest_session && (
                          <div className="text-right">
                            <p className="text-gray-900 dark:text-white">
                              Last: {timeAgo(user.latest_session.last_activity_at)}
                            </p>
                            <button
                              onClick={() => forceLogoutUser(user.id, user.name)}
                              disabled={user.id === currentUser?.id || !user.is_currently_online}
                              className={`px-3 py-1 rounded text-sm font-medium mt-1 ${
                                user.id === currentUser?.id
                                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-50'
                                  : !user.is_currently_online
                                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-50'
                                  : 'bg-red-500 hover:bg-red-600 text-white'
                              }`}
                              title={
                                user.id === currentUser?.id
                                  ? "You cannot logout yourself"
                                  : !user.is_currently_online
                                  ? "User is not currently online"
                                  : "Force logout this user"
                              }
                            >
                              {user.id === currentUser?.id ? 'You' : user.is_currently_online ? 'Force Logout' : 'Offline'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Pagination for users */}
                {usersPagination && usersPagination.last_page > 1 && (
                  <div className="flex justify-center space-x-2 mt-6">
                    <button
                      onClick={() => fetchUniqueUsers(usersPagination.current_page - 1)}
                      disabled={usersPagination.current_page === 1}
                      className="px-3 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-gray-700 dark:text-gray-300">
                      Page {usersPagination.current_page} of {usersPagination.last_page}
                    </span>
                    <button
                      onClick={() => fetchUniqueUsers(usersPagination.current_page + 1)}
                      disabled={usersPagination.current_page === usersPagination.last_page}
                      className="px-3 py-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSessionDashboard;
